/**
 * Public Slide Viewer Route
 *
 * Displays a publicly shared slide via share token.
 * No authentication required.
 * Full-screen presentation mode with playback controls.
 *
 * Based on GsapSlidePreviewEnhanced but without edit capabilities.
 */

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Loader2, Minimize2 } from 'lucide-react';
import { GSAP_VIEWER_CONFIG } from '../config/gsapViewer';
import { GsapPlayerControls, AutoHidePlayerControls, type AutoHidePlayerControlsRef } from '../components/slides/GsapPlayerControls';
import { cn } from '../lib/utils';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface SlideInfo {
  id: string;
  title: string;
  slideIdentifier: string;
}

interface PublicSlideResponse {
  success: boolean;
  slideInfo: SlideInfo;
  author: string | null;
  audio?: {
    audioUrl: string;
    audioDuration: number;
    wordTimings?: any;
  };
}

interface PlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isLoading: boolean;
  slideTitle: string;
}

export const Route = createFileRoute('/share/$shareToken')({
  component: PublicSlideViewer,
  loader: async ({ params }) => {
    const { shareToken } = params;

    if (!shareToken) {
      throw notFound();
    }

    try {
      // Fetch public slide info from API (validates the token)
      const response = await fetch(`${API_BASE_URL}/api/share/${shareToken}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw notFound();
        }
        throw new Error('Failed to load slide');
      }

      const data: PublicSlideResponse = await response.json();

      if (!data.success) {
        throw notFound();
      }

      // Return the share token and slide info
      // The GSAP viewer will fetch the actual slide.json from the public API endpoint
      return {
        shareToken,
        slideInfo: data.slideInfo,
        author: data.author,
        // Build the URL for the GSAP viewer to fetch the slide JSON
        slideJsonUrl: `${API_BASE_URL}/api/share/${shareToken}/slide.json`,
        // Include audio data if available
        audio: data.audio,
      };
    } catch (error) {
      console.error('Error loading public slide:', error);
      throw notFound();
    }
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="text-6xl">🔒</div>
        <h1 className="text-2xl font-bold text-foreground">Slide Not Found</h1>
        <p className="text-muted-foreground max-w-md">
          This share link may have expired, been revoked, or doesn't exist.
        </p>
      </div>
    </div>
  ),
});

function PublicSlideViewer() {
  const { slideInfo, slideJsonUrl, author, audio } = Route.useLoaderData();

  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<AutoHidePlayerControlsRef>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(0.5);
  const [playerState, setPlayerState] = useState<PlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    isLoading: true,
    slideTitle: slideInfo.title || 'Loading...',
  });

  // Build viewer URL - GSAP viewer will fetch the slide.json from the API
  // Use 'external' uiMode so the GSAP viewer broadcasts state to parent (React controls)
  // and hides native controls (we provide our own React-based controls)
  const viewerUrl = GSAP_VIEWER_CONFIG.buildUrl(slideJsonUrl, {
    editMode: false,
    controls: false,  // Hide native controls, we use React controls
    uiMode: 'external',  // Required for postMessage communication
  });

  // Send commands to iframe (same format as GsapSlidePreviewEnhanced)
  const sendCommand = useCallback((action: string, data?: any) => {
    if (iframeRef.current?.contentWindow) {
      const message = {
        source: 'gsap-preview',
        action,
        ...data,
      };
      console.log('📤 PublicSlideViewer - Sending command:', message);
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // Player control handlers - sync both animation and audio
  const handlePlayPause = useCallback(() => {
    sendCommand('togglePlay');
    // Audio sync handled by message handler when iframe reports state change
  }, [sendCommand]);

  const handlePlay = useCallback(() => {
    sendCommand('play');
    if (audioRef.current && audio?.audioUrl) {
      audioRef.current.play().catch(() => {});
    }
  }, [sendCommand, audio?.audioUrl]);

  const handlePause = useCallback(() => {
    sendCommand('pause');
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, [sendCommand]);

  const handleSeek = useCallback((time: number) => {
    sendCommand('seek', { time });
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, [sendCommand]);

  const handleRestart = useCallback(() => {
    sendCommand('restart');
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [sendCommand]);

  const handleStepBackward = useCallback(() => {
    sendCommand('step', { seconds: -10 });
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  }, [sendCommand]);

  const handleStepForward = useCallback(() => {
    sendCommand('step', { seconds: 10 });
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration || Infinity,
        audioRef.current.currentTime + 10
      );
    }
  }, [sendCommand]);

  const handleSpeedChange = useCallback((rate: number) => {
    sendCommand('setSpeed', { rate });
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, [sendCommand]);

  // Handle messages from iframe (GSAP viewer sends { source: 'gsap-renderer', state: {...} })
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from gsap-renderer
      if (event.data?.source !== 'gsap-renderer') return;

      // Update player state from iframe
      if (event.data.state) {
        const newState = event.data.state;

        // Sync audio with animation state
        if (audioRef.current && audio?.audioUrl) {
          // Sync play/pause state
          if (newState.isPlaying !== undefined) {
            if (newState.isPlaying && audioRef.current.paused) {
              audioRef.current.play().catch(() => {});
            } else if (!newState.isPlaying && !audioRef.current.paused) {
              audioRef.current.pause();
            }
          }
          // Sync playback rate
          if (newState.playbackRate !== undefined && audioRef.current.playbackRate !== newState.playbackRate) {
            audioRef.current.playbackRate = newState.playbackRate;
          }
        }

        setPlayerState(prev => {
          // Check if any value actually changed
          const hasChanges = Object.keys(newState).some(key =>
            prev[key as keyof typeof prev] !== newState[key]
          );

          return hasChanges ? { ...prev, ...newState } : prev;
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [audio?.audioUrl]);

  // Scale calculation for responsive iframe
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height - 100; // Account for header/controls

        const scaleX = containerWidth / GSAP_VIEWER_CONFIG.slideResolution.width;
        const scaleY = containerHeight / GSAP_VIEWER_CONFIG.slideResolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener('resize', updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Fullscreen handlers
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'min-h-screen bg-black flex flex-col',
        isFullscreen && 'fixed inset-0 z-50'
      )}
    >
      {/* Main Content - Centered Slide */}
      <div className="flex-1 relative overflow-hidden bg-black">
        {/* Viewport - Centered slide preview */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="relative bg-white rounded-lg overflow-hidden shadow-2xl"
            style={{
              width: `${GSAP_VIEWER_CONFIG.slideResolution.width * scale}px`,
              height: `${GSAP_VIEWER_CONFIG.slideResolution.height * scale}px`,
            }}
          >
            <iframe
              ref={iframeRef}
              src={viewerUrl}
              className="absolute inset-0 w-full h-full border-0"
              title={slideInfo.title || 'Slide'}
              sandbox="allow-same-origin allow-scripts allow-forms"
              style={{
                width: `${GSAP_VIEWER_CONFIG.slideResolution.width}px`,
                height: `${GSAP_VIEWER_CONFIG.slideResolution.height}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />

            {/* Loading Overlay */}
            {playerState.isLoading && (
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center text-white">
                  <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3" />
                  <p className="text-sm opacity-80">Loading slide...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom-edge trigger zone: shows player controls on hover
            without blocking slide interactive elements (Continue button) */}
        <div
          className="absolute left-0 right-0 bottom-0 h-3 z-20"
          onMouseEnter={() => controlsRef.current?.show()}
        />

        {/* Overlay Player Controls - Floating at Bottom */}
        <AutoHidePlayerControls
          ref={controlsRef}
          isPlaying={playerState.isPlaying}
          isFullscreen={isFullscreen}
          className="absolute bottom-8 left-0 right-0 z-20 px-4"
        >
          <GsapPlayerControls
            isPlaying={playerState.isPlaying}
            currentTime={playerState.currentTime}
            duration={playerState.duration}
            playbackRate={playerState.playbackRate}
            isLoading={playerState.isLoading}
            onPlayPause={handlePlayPause}
            onPlay={handlePlay}
            onPause={handlePause}
            onSeek={handleSeek}
            onRestart={handleRestart}
            onStepBackward={handleStepBackward}
            onStepForward={handleStepForward}
            onSpeedChange={handleSpeedChange}
            onFullscreen={handleFullscreen}
          />
        </AutoHidePlayerControls>

        {/* Fullscreen Exit Button */}
        {isFullscreen && (
          <Button
            onClick={handleFullscreen}
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 z-30 text-white/70 hover:text-white hover:bg-white/10"
          >
            <Minimize2 className="h-5 w-5" />
          </Button>
        )}

        {/* Hidden audio element for narration playback */}
        {audio?.audioUrl && (
          <audio
            ref={audioRef}
            src={audio.audioUrl}
            preload="auto"
            className="hidden"
          />
        )}
      </div>

      {/* Footer with Branding and Author */}
      {!isFullscreen && (
        <div className="flex-shrink-0 bg-black py-2 px-6 border-t border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <p className="text-white/40 text-xs">
              Powered by <span className="text-white/60">4K Labs</span>
            </p>
            {author && (
              <p className="text-white/40 text-xs">
                Created by <span className="text-white/60">{author}</span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
